import { useState } from 'react';
import VerticalNav from '../navigation/VerticalNav';
import { useLayout } from '../../contexts/LayoutContext';
import { useLanguage } from '../../contexts/LanguageContext';
import ThemeToggle from '../ui/ThemeToggle';

export default function SidebarLayout({ 
  children, 
  currentPage, 
  currentUser, 
  onNavigate,
  onOpenAuth,
  onLogout 
}) {
  const { sidebarCollapsed } = useLayout();
  const { t } = useLanguage();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="cv-app min-h-screen bg-[var(--bg-primary)] text-[var(--text-primary)] flex">
      <div className="hidden lg:block">
        <div className="fixed inset-y-0 left-0 z-40">
          <VerticalNav 
            currentPage={currentPage} 
            onNavigate={onNavigate}
            currentUser={currentUser}
          />
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div 
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setMobileMenuOpen(false)}
            aria-hidden="true"
          />
          <div className="absolute inset-y-0 left-0 w-64">
            <VerticalNav 
              currentPage={currentPage} 
              onNavigate={(path) => {
                onNavigate(path);
                setMobileMenuOpen(false);
              }}
              currentUser={currentUser}
            />
          </div>
        </div>
      )}

      <div className={`flex-1 flex flex-col transition-all duration-300 ${
        sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'
      }`}>
        <header className="sticky top-0 z-30 bg-[var(--bg-elevated)]/80 backdrop-blur-xl border-b border-[var(--border-default)]">
          <div className="flex items-center justify-between px-4 lg:px-6 h-14">
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={() => setMobileMenuOpen(true)}
                className="lg:hidden p-2 hover:bg-[var(--surface-hover)] rounded-lg text-[var(--text-muted)]"
                aria-label={t('sidebar.expandMenu')}
                aria-expanded={mobileMenuOpen}
              >
                <span className="text-xl">☰</span>
              </button>
              <div className="lg:hidden flex items-center gap-2">
                <span className="text-xl">📈</span>
                <span className="text-lg font-bold text-[var(--text-primary)]">ControlVitals</span>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <ThemeToggle className="text-[var(--text-muted)] hover:text-[var(--text-primary)]" />

              {currentUser ? (
                <div className="flex items-center gap-2">
                  <div className="hidden sm:block text-right">
                    <div className="text-sm font-medium text-[var(--text-primary)]">{currentUser.username}</div>
                    <div className="text-xs text-[var(--text-muted)] capitalize">
                      {typeof currentUser.tier === 'object' ? currentUser.tier?.name || currentUser.tier?.slug : currentUser.tier}
                      {currentUser.subscription_status && currentUser.subscription_status !== 'active' && (
                        <span className="ml-1 text-amber-400">({currentUser.subscription_status})</span>
                      )}
                    </div>
                    {currentUser.current_period_end && (
                      <div className="text-[10px] text-[var(--text-muted)]">
                        {currentUser.cancel_at_period_end ? t('sidebar.subscriptionEnds') : t('sidebar.nextBilling')}
                        {new Date(currentUser.current_period_end).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                  {currentUser.stripe_customer_id && (
                    <>
                      <button
                        onClick={async () => {
                          try {
                            const userApi = (await import('../../services/api.js')).userApi;
                            const result = await userApi.openBillingPortal();
                            if (result?.url) {
                              window.location.href = result.url;
                            } else {
                              alert(result?.error || 'Otevření portálu selhalo');
                            }
                          } catch (e) {
                            alert(e.message || 'Otevření portálu selhalo');
                          }
                        }}
                        className="p-2 hover:bg-gray-700/50 rounded-lg transition-colors text-gray-400 hover:text-white"
                        title={t('sidebar.manageBilling')}
                      >
                        💳
                      </button>
                      {currentUser.stripe_subscription_id && !currentUser.cancel_at_period_end && (
                        <button
                          onClick={async () => {
                            if (!confirm(t('sidebar.cancelSubscriptionConfirm'))) return;
                            try {
                              const userApi = (await import('../../services/api.js')).userApi;
                              const r = await userApi.changeTier('free', currentUser.billing_cycle || 'monthly', true);
                              if (r?.success) {
                                alert(r.message || t('sidebar.cancelSubscriptionDone'));
                                window.location.reload();
                              } else {
                                alert(r?.error || 'Zrušení selhalo');
                              }
                            } catch (e) {
                              alert(e.message || 'Zrušení selhalo');
                            }
                          }}
                          className="p-2 hover:bg-gray-700/50 rounded-lg transition-colors text-gray-400 hover:text-red-400"
                          title={t('sidebar.cancelSubscription')}
                        >
                          ✖
                        </button>
                      )}
                    </>
                  )}
                  <button
                    onClick={onLogout}
                    className="p-2 hover:bg-gray-700/50 rounded-lg transition-colors text-gray-400 hover:text-white"
                    title={t('sidebar.signOut')}
                  >
                    🚪
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => onOpenAuth?.('login')}
                  className="px-4 py-2 bg-gradient-to-r from-purple-600 to-cyan-600 text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity"
                >
                  {t('sidebar.signIn')}
                </button>
              )}
            </div>
          </div>
        </header>

        <main className="flex-1 p-4 lg:p-6">
          {children}
        </main>

        <footer className="border-t border-[var(--border-default)] py-4 px-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-[var(--text-muted)]">
            <div>© {new Date().getFullYear()} ControlVitals. {t('sidebar.allRightsReserved')}</div>
            <div className="flex items-center gap-4">
              <a href="/privacy" className="hover:text-[var(--text-primary)] transition-colors">{t('sidebar.privacy')}</a>
              <a href="/terms" className="hover:text-[var(--text-primary)] transition-colors">{t('sidebar.terms')}</a>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}
